import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { usePCAStore } from '../stores/pcaStore';

export function HyperslicerPanel() {
  const { 
    slices, 
    pcaData,
    addSlice, 
    updateSlice,
    getDimensionRange,
    getDimensionName,
    getDimensionLabels,
    getTotalDimensions
  } = usePCAStore();

  // Get the active slice (we only have one now) - check early
  const slice = slices[0];

  // Local state for immediate UI updates
  const [localDimension, setLocalDimension] = useState<number | null>(null);
  const [localPosition, setLocalPosition] = useState<number | null>(null);
  const [localWidth, setLocalWidth] = useState<number | null>(null);
  
  // Debounce timers
  const dimensionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const positionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const widthTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Store slice ID in ref to avoid dependency issues
  const sliceIdRef = useRef<string | null>(null);
  
  // Update ref when slice changes
  useEffect(() => {
    sliceIdRef.current = slice?.id ?? null;
  }, [slice]);

  // Dimensions 1-3 are fixed as X/Y/Z axes, so sliceable dims start at 4
  const totalDimensions = getTotalDimensions();
  const minSliceDim = 4;
  const maxSliceDim = totalDimensions;

  // Initialize with default slice on dimension 4 when data loads
  useEffect(() => {
    if (pcaData && slices.length === 0) {
      addSlice(4);
    }
  }, [pcaData, slices.length, addSlice]);

  // Sync local state with slice state
  useEffect(() => {
    if (slice) {
      setLocalDimension(null);
      setLocalPosition(null);
      setLocalWidth(null);
    }
  }, [slice]);
  
  // Use local state if available, otherwise use slice state
  const displayDimension = localDimension !== null ? localDimension : (slice?.dimension ?? 4);
  const displayPosition = localPosition !== null ? localPosition : (slice?.position ?? 0);
  const displayWidth = localWidth !== null ? localWidth : (slice?.width ?? 0.3);
  
  // Get range for the display dimension (memoized to avoid recalculation during drag)
  const range = useMemo(() => {
    return slice ? getDimensionRange(displayDimension) : { min: -1, max: 1 };
  }, [slice, displayDimension, getDimensionRange]);
  
  const rangeSize = range.max - range.min || 1;
  
  // Clamp position to valid range when dimension changes
  const clampedPosition = Math.max(range.min, Math.min(range.max, displayPosition));

  // Debounced handlers with longer delays to prevent blocking
  const handleDimensionChange = useCallback((value: number) => {
    setLocalDimension(value);
    
    // Reset position to midpoint of new dimension for immediate UI feedback
    const newRange = getDimensionRange(value);
    const midpoint = (newRange.min + newRange.max) / 2;
    setLocalPosition(midpoint);
    
    if (dimensionTimerRef.current) {
      clearTimeout(dimensionTimerRef.current);
    }
    
    dimensionTimerRef.current = setTimeout(() => {
      const sliceId = sliceIdRef.current;
      if (sliceId) {
        updateSlice(sliceId, { dimension: value });
      }
      setLocalDimension(null);
      setLocalPosition(null);
    }, 500); // 500ms debounce for dimension changes (most expensive)
  }, [updateSlice, getDimensionRange]);

  const handlePositionChange = useCallback((value: number) => {
    setLocalPosition(value);
    
    if (positionTimerRef.current) {
      clearTimeout(positionTimerRef.current);
    }
    
    positionTimerRef.current = setTimeout(() => {
      const sliceId = sliceIdRef.current;
      if (sliceId) {
        updateSlice(sliceId, { position: value });
      }
      setLocalPosition(null);
    }, 200); // 200ms debounce
  }, [updateSlice]);

  const handleWidthChange = useCallback((value: number) => {
    setLocalWidth(value);
    
    if (widthTimerRef.current) {
      clearTimeout(widthTimerRef.current);
    }
    
    widthTimerRef.current = setTimeout(() => {
      const sliceId = sliceIdRef.current;
      if (sliceId) {
        updateSlice(sliceId, { width: value });
      }
      setLocalWidth(null);
    }, 200); // 200ms debounce
  }, [updateSlice]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (dimensionTimerRef.current) clearTimeout(dimensionTimerRef.current);
      if (positionTimerRef.current) clearTimeout(positionTimerRef.current);
      if (widthTimerRef.current) clearTimeout(widthTimerRef.current);
    };
  }, []);

  if (!slice) {
    return (
      <div className="p-4">
        <p className="text-sm text-text-secondary">Loading...</p>
      </div>
    );
  }

  const dimName = getDimensionName(displayDimension);
  const dimLabels = getDimensionLabels(displayDimension);

  return (
    <div className="p-4 pt-3">
      {/* Hyperslice header */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-text-secondary font-medium">Hyperslice</span>
        <span className="text-xs text-orange-primary bg-orange-primary/10 px-2 py-0.5 rounded-full font-medium">
          Dim {slice.dimension}
        </span>
      </div>

      <p className="text-xs text-text-secondary/70 mb-3">
        Slice through the hyperspace to reveal structure.
      </p>

      {/* Current dimension name */}
      <div className="bg-warm-surface-hover/50 rounded-lg p-2 mb-4">
        <div className="text-xs text-text-secondary mb-1">Current Dimension:</div>
        <div className="text-sm text-text-primary font-medium leading-tight">
          {dimName}
        </div>
      </div>

      {/* Dimension selector */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-text-secondary">Dimension</span>
          <span className="text-sm font-bold text-orange-primary">{displayDimension}</span>
        </div>
        <input
          type="range"
          min={minSliceDim}
          max={maxSliceDim}
          step={1}
          value={displayDimension}
          onChange={(e) => handleDimensionChange(parseInt(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-text-secondary mt-1">
          <span>{minSliceDim}</span>
          <span>{maxSliceDim}</span>
        </div>
      </div>

      {/* Position slider */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-text-secondary">Position</span>
          <span className="text-sm font-mono text-text-primary">{clampedPosition.toFixed(2)}</span>
        </div>
        <input
          type="range"
          min={range.min}
          max={range.max}
          step={rangeSize / 100}
          value={clampedPosition}
          onChange={(e) => handlePositionChange(parseFloat(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-text-secondary mt-1">
          <span className="truncate max-w-[45%]" title={dimLabels.low}>{dimLabels.low}</span>
          <span className="truncate max-w-[45%] text-right" title={dimLabels.high}>{dimLabels.high}</span>
        </div>
      </div>

      {/* Width/tolerance slider */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-text-secondary">Width</span>
          <span className="text-sm font-mono text-text-primary">{(displayWidth * 100).toFixed(0)}%</span>
        </div>
        <input
          type="range"
          min={0.05}
          max={1}
          step={0.05}
          value={displayWidth}
          onChange={(e) => handleWidthChange(parseFloat(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-text-secondary mt-1">
          <span>Narrow</span>
          <span>Wide</span>
        </div>
      </div>
    </div>
  );
}
